import React from 'react';
import {
  AppContent,
  Banner,
  BaseButton,
  ErrorHandler,
  Icon,
  LoadingState,
  Link,
} from '@common/components';
import { Col, Row } from 'antd';
import { useQuery } from '@common/hooks';
import {
  InvoiceHoursTab,
  InvoiceReviewQueryArgs,
  InvoiceReviewQueryResponse,
  RevInvoiceHoursTab,
  RevInvoiceReviewQueryArgs,
  RevInvoiceReviewQueryResponse,
} from '@pay-bill/graphql';
import { BillingInvoiceItemsList } from '@pay-bill/components';
import { toUSCompactDateFormat } from 'works/legacy/lib/date-helper';
import { buildDialogUrl } from '@common/services';

export const BillingHoursTab = ({
  id,
  isSupplier,
}: {
  id: string;
  isSupplier: boolean;
}) => {
  const {
    data: revInvoiceData,
    loading: revInvoiceLoading,
    error: revInvoiceError,
  } = useQuery<RevInvoiceReviewQueryResponse, RevInvoiceReviewQueryArgs>(
    RevInvoiceHoursTab,
    {
      variables: { id },
      skip: !isSupplier,
    }
  );

  const {
    data: invoiceData,
    loading: invoiceLoading,
    error: invoiceError,
  } = useQuery<InvoiceReviewQueryResponse, InvoiceReviewQueryArgs>(
    InvoiceHoursTab,
    {
      variables: { id },
      skip: isSupplier,
    }
  );

  const error = revInvoiceError || invoiceError;
  if (error) {
    return <ErrorHandler error={error} log="billing-hours-tab" />;
  }

  if (revInvoiceLoading || invoiceLoading) {
    return <LoadingState />;
  }

  const invoice =
    revInvoiceData?.worksReverseInvoice?.worksInvoice ||
    invoiceData?.worksInvoice;
  const canSubmitDispute = invoice?.canSubmitDispute?.value;

  const missingLineDrawerUrl = buildDialogUrl(
    'dispute-create-missing-line-drawer',
    {
      props: { id },
    }
  );

  return (
    <>
      <AppContent left={false} right={false} top={false}>
        <Row align="middle" className="billing-hours-tab__info-header">
          <Col span={21}>
            {invoice?.disputeClosingDate && (
              <Banner
                type="info"
                content={`Disputes can no longer be submitted after ${toUSCompactDateFormat(invoice?.disputeClosingDate)}`}
                closable
              />
            )}
          </Col>
          {canSubmitDispute && (
            <Col span={3}>
              <Row justify="end">
                <Link to={missingLineDrawerUrl}>
                  <BaseButton>
                    <Icon icon="plus" />
                    Missing Line
                  </BaseButton>
                </Link>
              </Row>
            </Col>
          )}
        </Row>
        <BillingInvoiceItemsList
          entity={{ id: id }}
          viewMode={isSupplier ? 'Supplier' : 'Organization'}
        />
      </AppContent>
      <style jsx>{`
        :global(.billing-hours-tab__info-header) {
          margin-top: 12px;
        }
      `}</style>
    </>
  );
};
